/**
 * OrgWizard — create or edit any organisation (ATOMS / OEM / Distributor).
 * Minimal typing: org type and parent OEM are dropdowns, colour is a picker.
 */
import React, { useEffect, useState } from 'react';
import {
  Drawer, Stack, TextInput, Select, ColorInput, Switch,
  Button, Group, Alert, Text, Image, Box,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { createOrg, updateOrg, getOrgs } from '../../API/adminAPI';

const ORG_TYPES = [
  { value: 'atoms', label: 'ATOMS (Super Admin org)' },
  { value: 'oem',   label: 'OEM / Manufacturer' },
  { value: 'distributor', label: 'Distributor / Reseller' },
];

const OrgWizard = ({ org, onClose }) => {
  const isEdit = Boolean(org);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [oems, setOems] = useState([]);

  useEffect(() => {
    // Load OEM list for parent_oem dropdown
    getOrgs()
      .then((d) => {
        const list = (d.organisations || [])
          .filter((o) => o.org_type === 'oem')
          .map((o) => ({ value: String(o.id), label: o.name }));
        setOems(list);
      })
      .catch(() => {});
  }, []);

  const form = useForm({
    initialValues: {
      name:          org?.name || '',
      org_type:      org?.org_type || 'oem',
      parent_oem_id: org?.parent_oem_id ? String(org.parent_oem_id) : null,
      logo_url:      org?.logo_url || '',
      primary_color: org?.primary_color || '#1F4E79',
      is_active:     org?.is_active ?? true,
    },
    validate: {
      name:     (v) => (v.trim() ? null : 'Name is required'),
      org_type: (v) => (v ? null : 'Select an org type'),
    },
  });

  // Auto-generate slug from name
  const toSlug = (name) =>
    name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const save = async () => {
    if (form.validate().hasErrors) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        name:          form.values.name.trim(),
        slug:          toSlug(form.values.name),
        org_type:      form.values.org_type,
        parent_oem_id: form.values.parent_oem_id ? parseInt(form.values.parent_oem_id) : null,
        logo_url:      form.values.logo_url.trim() || '',
        primary_color: form.values.primary_color,
        is_active:     form.values.is_active,
      };
      if (isEdit) await updateOrg(org.id, payload);
      else await createOrg(payload);
      onClose(true);
    } catch (e) {
      setError(e?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const logoPreview = form.values.logo_url;

  return (
    <Drawer
      opened
      onClose={() => onClose(false)}
      title={isEdit ? `Edit — ${org.name}` : 'New Organisation'}
      size="md"
      position="right"
      padding="xl"
      closeOnClickOutside={false}
    >
      {error && <Alert color="red" mb="sm">{error}</Alert>}

      <Stack spacing="sm">
        <TextInput
          label="Organisation Name"
          placeholder="Maithri Aquatech"
          required
          {...form.getInputProps('name')}
        />

        <Select
          label="Type"
          data={ORG_TYPES}
          required
          disabled={isEdit}
          description={isEdit ? 'Type cannot be changed after creation' : ''}
          {...form.getInputProps('org_type')}
        />

        {form.values.org_type === 'distributor' && (
          <Select
            label="Parent OEM"
            placeholder="Select OEM this distributor belongs to"
            data={oems}
            clearable
            {...form.getInputProps('parent_oem_id')}
          />
        )}

        <TextInput
          label="Logo URL"
          placeholder="https://res.cloudinary.com/…/logo.png"
          description="Shown in top bar for all users in this org"
          {...form.getInputProps('logo_url')}
        />

        {logoPreview && (
          <Box sx={{ border: '1px solid #dee2e6', borderRadius: 6, padding: 8, textAlign: 'center' }}>
            <Text size="xs" color="dimmed" mb={4}>Preview</Text>
            <Image src={logoPreview} height={40} fit="contain" alt="logo preview" />
          </Box>
        )}

        <ColorInput
          label="Brand Colour"
          description="Primary accent colour for this org's UI"
          {...form.getInputProps('primary_color')}
        />

        <Switch
          label="Active"
          description="Inactive orgs — all their users are locked out immediately"
          checked={form.values.is_active}
          onChange={(e) => form.setFieldValue('is_active', e.currentTarget.checked)}
        />
      </Stack>

      <Group position="apart" mt="xl">
        <Button variant="subtle" onClick={() => onClose(false)}>Cancel</Button>
        <Button
          onClick={save}
          loading={saving}
          sx={{ backgroundColor: '#1F4E79', '&:hover': { backgroundColor: '#163a5f' } }}
        >
          {isEdit ? 'Save Changes' : 'Create Organisation'}
        </Button>
      </Group>
    </Drawer>
  );
};

export default OrgWizard;
